import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CancelIcon from "@mui/icons-material/Cancel";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import { useMaterialUIController } from "context";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { puedeAccion } from "lib/seguridad/permisosPorModulo";
import { useApi, useSucursales } from "lib/api";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
} from "components/financepro";

function Cobrosincautosfechas() {
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState("");
  const [rows, setRows] = useState([]);
  const [estatuspago, setEstatuspago] = useState("No Cobrado");
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [filtroNombre, setFiltroNombre] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editValor, setEditValor] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { post } = useApi();
  const sucursal = useSucursales();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const puedeModificar = puedeAccion(roleDetalles, "motores", "modificar");
  const puedeBorrar = puedeAccion(roleDetalles, "motores", "borrar");

  const cobrosColumns = useMemo(
    () => [
      { field: "id", headerName: "Préstamo #", width: 110, align: "center", headerAlign: "center" },
      {
        field: "fecha",
        headerName: "Fecha",
        type: "date",
        width: 115,
        align: "center",
        headerAlign: "center",
        editable: true,
        valueGetter: (value) => {
          const v = value;
          if (v == null || v === "") return null;
          if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
          const s = String(v).trim();
          const mDmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
          if (mDmy) {
            const [, dd, mm, yyyy] = mDmy;
            const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
            return Number.isNaN(d.getTime()) ? null : d;
          }
          const mYmd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (mYmd) {
            const [, yyyy, mm, dd] = mYmd;
            const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
            return Number.isNaN(d.getTime()) ? null : d;
          }
          const d = new Date(s);
          return Number.isNaN(d.getTime()) ? null : d;
        },
        valueFormatter: (value) => {
          if (!value) return "";
          const d = value instanceof Date ? value : new Date(value);
          if (Number.isNaN(d.getTime())) return "";
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const yyyy = d.getFullYear();
          return `${dd}-${mm}-${yyyy}`;
        },
      },
      { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 130, editable: true },
      { field: "apellidos", headerName: "Apellidos", type: "string", flex: 1, minWidth: 130, editable: true },
      { field: "cedula", headerName: "Cédula", type: "string", minWidth: 120, flex: 0.6 },
      { field: "fechacobro", headerName: "Fecha Cobro", type: "string", minWidth: 115 },
      { field: "incautador", headerName: "Incautador", type: "string", flex: 1, minWidth: 120 },
      { field: "chasis", headerName: "Chasis", type: "string", minWidth: 150, flex: 1 },
      {
        field: "valor",
        headerName: "Valor",
        type: "number",
        minWidth: 120,
        flex: 0.7,
        align: "right",
        headerAlign: "right",
        valueGetter: (value) => {
          const v = value;
          if (v == null || v === "") return 0;
          const n = parseFloat(String(v).replace(/,/g, "").trim());
          return Number.isNaN(n) ? 0 : n;
        },
        valueFormatter: (value) => {
          const n = Number(value || 0);
          return `$${n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
      },
      ...(puedeModificar || puedeBorrar
        ? [
            {
              field: "acciones",
              headerName: "Acciones",
              width: 110,
              sortable: false,
              filterable: false,
              align: "center",
              headerAlign: "center",
              renderCell: (params) => {
                const fc = params.row?.fechacobro;
                const isNoCobrado = !fc || fc === "" || fc === "00-00-0000";
                if (!isNoCobrado) return null;
                return (
                  <Stack direction="row" spacing={0.5}>
                    {puedeModificar && (
                      <IconButton size="small" color="primary" onClick={() => abrirEditarValor(params.row)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {puedeBorrar && (
                      <IconButton size="small" color="error" onClick={() => abrirConfirmarCancelar(params.row)}>
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                );
              },
            },
          ]
        : []),
    ],
    [puedeModificar, puedeBorrar]
  );

  function abrirEditarValor(row) {
    setEditTarget(row);
    const v = parseFloat(String(row.valor || "0").replace(/,/g, ""));
    setEditValor(Number.isNaN(v) ? "" : String(v));
    setEditOpen(true);
  }

  async function guardarValor() {
    if (!editTarget) return;
    const valorNum = parseFloat(editValor);
    if (Number.isNaN(valorNum) || valorNum < 0) {
      alert("Ingresa un valor numérico válido");
      return;
    }
    try {
      const { status, data } = await post("/api/actualizarvalorincauto", {
        chasis: editTarget.chasis,
        noprest: editTarget.id,
        valor: valorNum,
      });
      if (status === 200) {
        setRows((prev) => prev.map((r) => (r.id === editTarget.id && r.chasis === editTarget.chasis) ? { ...r, valor: valorNum.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) } : r));
        setEditOpen(false);
        setEditTarget(null);
        alert("Valor actualizado");
      } else {
        alert(data?.Error || "No se pudo actualizar el valor");
      }
    } catch (err) {
      alert("Error de red: " + err.message);
    }
  }

  function abrirConfirmarCancelar(row) {
    setConfirmTarget(row);
    setConfirmOpen(true);
  }

  async function cancelarIncauto() {
    if (!confirmTarget) return;
    try {
      const { status, data } = await post("/api/eliminarregistroincauto", {
        chasis: confirmTarget.chasis,
        noprest: confirmTarget.id,
      });
      if (status === 200) {
        setRows((prev) => prev.filter((r) => !(r.id === confirmTarget.id && r.chasis === confirmTarget.chasis)));
        setConfirmOpen(false);
        setConfirmTarget(null);
        alert("Incauto cancelado");
      } else {
        alert(data?.Error || "No se pudo cancelar el incauto");
        setConfirmOpen(false);
      }
    } catch (err) {
      alert("Error de red: " + err.message);
      setConfirmOpen(false);
    }
  }

  async function procesarrequerimiento() {
    const { status, data } = await post("/api/cobrosincautosfechas", {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: nosucursal,
      estatus: estatuspago,
    });
    if (status === 200) {
      setRows(data["misincautos"]);
      setFiltroNombre("");
    }
    if (status === 400) {
      setRows([]);
      alert(data["Error"]);
    }
  }

  const MyDocument = ({ rowsData, nombreNegocio1 }) => {
    const totalCobro = (rowsData || []).reduce(
      (sum, row) => sum + (parseFloat(String(row.valor || 0).replace(/,/g, "").trim()) || 0),
      0
    );

    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
        <Document>
          <Page size="A4" orientation="Portrait" wrap style={{ backgroundColor: "#FFFFFF", paddingBottom: 65 }}>
            <View style={{ textAlign: "center", width: "94%", fontSize: 10, textTransform: "uppercase", marginTop: "15px" }} fixed>
              <Text>{nombreNegocio1 || "Empresa"}</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text>Reporte Cobros Incautos</Text>
            </View>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 10, backgroundColor: "#1976d2", color: "white", marginLeft: "15px" }} fixed>
              <Text style={{ width: "55px", textAlign: "left", fontWeight: "bold" }}>Prest#</Text>
              <Text style={{ width: "65px", textAlign: "left", fontWeight: "bold" }}>Fecha</Text>
              <Text style={{ width: "85px", textAlign: "left", fontWeight: "bold" }}>Nombres</Text>
              <Text style={{ width: "85px", textAlign: "left", fontWeight: "bold" }}>Apellidos</Text>
              <Text style={{ width: "75px", textAlign: "left", fontWeight: "bold" }}>Cedula</Text>
              <Text style={{ width: "65px", textAlign: "left", fontWeight: "bold" }}>F.Cobro</Text>
              <Text style={{ width: "80px", textAlign: "left", fontWeight: "bold" }}>Incautador</Text>
              <Text style={{ width: "85px", textAlign: "left", fontWeight: "bold" }}>Chasis</Text>
              <Text style={{ width: "75px", textAlign: "right", fontWeight: "bold" }}>Valor</Text>
            </View>
            {(rowsData || []).map((filas, i) => (
              <View key={i} style={{ flexDirection: "row", width: "94%", height: "15px", fontSize: 9, color: "black", marginLeft: "15px", backgroundColor: i % 2 === 0 ? "#f9f9f9" : "white" }}>
                <Text style={{ width: "55px", textAlign: "left" }}>{filas.id}</Text>
                <Text style={{ width: "65px", textAlign: "left" }}>{filas.fecha}</Text>
                <Text style={{ width: "85px", textAlign: "left" }}>{filas.nombres}</Text>
                <Text style={{ width: "85px", textAlign: "left" }}>{filas.apellidos}</Text>
                <Text style={{ width: "75px", textAlign: "left" }}>{filas.cedula}</Text>
                <Text style={{ width: "65px", textAlign: "left" }}>{filas.fechacobro}</Text>
                <Text style={{ width: "80px", textAlign: "left" }}>{filas.incautador}</Text>
                <Text style={{ width: "85px", textAlign: "left" }}>{filas.chasis}</Text>
                <Text style={{ width: "75px", textAlign: "right" }}>{filas.valor}</Text>
              </View>
            ))}
            <Text> </Text>
            <View style={{ display: "flex", flexDirection: "row", width: "94%", height: "35px", fontSize: 10, backgroundColor: "#f5f5f5", color: "black", marginLeft: "15px", fontWeight: "bold", borderTop: "2px solid #1976d2" }}>
              <Text style={{ width: "55px", textAlign: "left" }}>Total</Text>
              <Text style={{ width: "65px", textAlign: "left" }}> </Text>
              <Text style={{ width: "85px", textAlign: "left" }}> </Text>
              <Text style={{ width: "85px", textAlign: "left" }}> </Text>
              <Text style={{ width: "75px", textAlign: "left" }}> </Text>
              <Text style={{ width: "65px", textAlign: "left" }}> </Text>
              <Text style={{ width: "80px", textAlign: "left" }}> </Text>
              <Text style={{ width: "85px", textAlign: "left" }}> </Text>
              <Text style={{ width: "75px", textAlign: "right" }}>
                {totalCobro.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  const rowsFiltradas = useMemo(() => {
    const q = filtroNombre.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const n = String(r.nombres ?? "").toLowerCase();
      const a = String(r.apellidos ?? "").toLowerCase();
      return n.includes(q) || a.includes(q);
    });
  }, [rows, filtroNombre]);

  const totalCobro = rowsFiltradas.reduce(
    (sum, row) => sum + (parseFloat(String(row.valor || 0).replace(/,/g, "").trim()) || 0),
    0
  );

  function handlepdf() {
    if (rowsFiltradas.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Motores"
        subtitle="Cobros Incautos por Fechas"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={rowsFiltradas.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
            <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
              Rango · Sucursal · Estatus
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  onChange={(event, value) => setNosucursal(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="estatus-pago-label" shrink>
                    Status
                  </InputLabel>
                  <Select
                    labelId="estatus-pago-label"
                    label="Status"
                    notched
                    value={estatuspago}
                    onChange={(event) => setEstatuspago(event.target.value)}
                  >
                    <MenuItem value="Cobrado">Cobrado</MenuItem>
                    <MenuItem value="No Cobrado">No Cobrado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rowsFiltradas.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Valor total incautos"
              value={`$${totalCobro.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AttachMoneyIcon}
              accent={FP.color.navy}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Listado de cobros incautos
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
              {rows.length > 0 && (
                <TextField
                  size="small"
                  placeholder="Buscar por nombre o apellido"
                  value={filtroNombre}
                  onChange={(e) => setFiltroNombre(e.target.value)}
                  sx={{
                    minWidth: { xs: "100%", sm: 260 },
                    "& .MuiOutlinedInput-root": {
                      height: 36,
                      borderRadius: 1.2,
                      bgcolor: "#FFFFFF",
                      fontSize: "0.86rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {rowsFiltradas.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handlepdf()}
                  sx={{
                    borderColor: FP.color.danger,
                    color: FP.color.danger,
                    borderRadius: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                  }}
                >
                  Generar PDF
                </Button>
              )}
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              <>
                <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                  <DataGrid
                    rows={rowsFiltradas}
                    columns={cobrosColumns}
                    getRowId={(row) =>
                      row.id != null ? String(row.id) : `${row.chasis ?? ""}-${row.fecha ?? ""}-${row.cedula ?? ""}`
                    }
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 720, sm: "100%" }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                  {rowsFiltradas.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `card-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                            {[row.nombres, row.apellidos].filter(Boolean).join(" ") || "—"}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                            {row.cedula || "—"}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Valor
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "1rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                            {row.valor ?? "—"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["F. cobro", row.fechacobro],
                          ["Incautador", row.incautador],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {row.chasis ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Chasis
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, wordBreak: "break-all" }}>
                            {row.chasis}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument rowsData={rowsFiltradas} nombreNegocio1={nombreNegocio} />
            </DialogContent>
          </Dialog>
        )}

        {/* Dialog: Editar valor del incauto */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Editar valor del incauto</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Chasis: <strong>{editTarget?.chasis || "—"}</strong>
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Nuevo valor"
              type="number"
              value={editValor}
              onChange={(e) => setEditValor(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button variant="contained" color="primary" onClick={guardarValor}>Guardar</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Confirmar cancelación de incauto */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Cancelar incauto</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Seguro que deseas cancelar el incauto del chasis <strong>{confirmTarget?.chasis || ""}</strong>? Esta acción eliminará el registro y liberará el chasis.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>No</Button>
            <Button color="error" variant="contained" onClick={cancelarIncauto}>Sí, cancelar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
export default Cobrosincautosfechas;
